import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RotateCcw } from 'lucide';
import { NbButton } from '../button/button';
import { NbIcon } from '../../icon/icon';

/**
 * `<nb-error-state>` — the "something failed, try again" pattern (design.md §5.5).
 *
 * A centred column with a human `title` (never a raw error code), projected
 * explanatory message, and a built-in "Prøv igjen" `<nb-button>` (with a
 * `RotateCcw` icon) that emits `(retry)`. The host is `role="alert"` so the
 * failure is announced assertively the moment it renders.
 *
 * @example
 * <nb-error-state title="Kunne ikke laste oppslag" (retry)="reload()">
 *   Sjekk nettforbindelsen og prøv igjen.
 * </nb-error-state>
 */
@Component({
  selector: 'nb-error-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbButton, NbIcon],
  host: {
    class: 'error',
    role: 'alert',
  },
  template: `@if (level() === 3) {
      <h3 class="title">{{ title() }}</h3>
    } @else {
      <h2 class="title">{{ title() }}</h2>
    }
    <p class="msg"><ng-content /></p>
    <nb-button variant="sec" (click)="retry.emit()">
      <nb-icon [icon]="RotateCcw" />
      Prøv igjen
    </nb-button>`,
  styleUrl: './error-state.css',
})
export class NbErrorState {
  /** Human-readable headline. Defaults to a friendly Norwegian fallback. */
  readonly title = input<string>('Noe gikk galt');
  /** Heading level of the title. Default `2` (a page's next level after its h1). */
  readonly level = input<2 | 3>(2);
  /** Fired when the user activates the built-in retry button. */
  readonly retry = output<void>();

  protected readonly RotateCcw = RotateCcw;
}
